import { useState, useEffect } from 'react';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { useBulkLocks } from '../hooks/useBulkLocks';
import { useAuth } from '../contexts/AuthContext';
import { LockIndicator } from '../components/permissions/LockIndicator';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { UserCheck, Search, Truck, Eye, Loader2, Calendar, Trash2, ClipboardList, Package, CreditCard, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { format, differenceInCalendarDays, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { ReadOnlyGuard } from '../components/permissions/ReadOnlyGuard';
import { subscribeDataChange } from '../lib/dataBus';
import { listsEqualById } from '../lib/listDiff';

const API = process.env.REACT_APP_BACKEND_URL;

const LIVRAISON_STATUTS = {
    sans_livraison: { label: 'Sans livraison', color: 'bg-slate-100 text-slate-600' },
    a_programmer: { label: 'A programmer', color: 'bg-amber-100 text-amber-700' },
    deja_programmee: { label: 'Deja programmee', color: 'bg-blue-100 text-blue-700' },
    termine: { label: 'Termine', color: 'bg-emerald-100 text-emerald-700' },
};

const ACOMPTE_STATUTS = {
    a_regle: { label: 'A regler', color: 'bg-amber-100 text-amber-700' },
    effectue: { label: 'Acompte regle', color: 'bg-emerald-100 text-emerald-700' },
};

const CLIENT_STATUTS = {
    a_verifier: { label: 'A verifier', color: 'bg-amber-100 text-amber-700' },
    pret: { label: 'Pret', color: 'bg-emerald-100 text-emerald-700' },
    annule: { label: 'Annule', color: 'bg-red-100 text-red-700' },
};

function formatDate(dateStr) {
    if (!dateStr) return '-';
    try { return format(new Date(dateStr), 'dd/MM/yyyy', { locale: fr }); } catch { return dateStr; }
}

function getDayLabel(dateStr) {
    if (!dateStr) return '';
    try {
        const diff = differenceInCalendarDays(parseISO(dateStr), new Date());
        if (diff === 0) return "Aujourd'hui";
        if (diff === 1) return 'Demain';
        if (diff === -1) return 'Hier';
        if (diff > 1) return `J+${diff}`;
        return `J${diff}`;
    } catch { return ''; }
}

function getIconColor(dateStr, statut) {
    if (statut === 'annule') return 'text-red-500';
    if (!dateStr) return 'text-slate-400';
    try {
        const diff = differenceInCalendarDays(parseISO(dateStr), new Date());
        if (diff >= 0) return 'text-[#2e7cd6]';
        return 'text-orange-500';
    } catch { return 'text-slate-400'; }
}

function sortClients(clients) {
    const now = new Date();
    return [...clients].sort((a, b) => {
        const statA = a.statut_client, statB = b.statut_client;
        if (statA === 'annule' && statB !== 'annule') return 1;
        if (statB === 'annule' && statA !== 'annule') return -1;

        const dateA = a.date_demenagement ? parseISO(a.date_demenagement) : null;
        const dateB = b.date_demenagement ? parseISO(b.date_demenagement) : null;
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;

        const diffA = differenceInCalendarDays(dateA, now);
        const diffB = differenceInCalendarDays(dateB, now);

        if (diffA === 0 && diffB !== 0) return -1;
        if (diffB === 0 && diffA !== 0) return 1;
        if (diffA > 0 && diffB > 0) return diffA - diffB;
        if (diffA > 0 && diffB < 0) return -1;
        if (diffA < 0 && diffB > 0) return 1;
        if (diffA < 0 && diffB < 0) return diffB - diffA;
        return 0;
    });
}

export default function ClientsPage() {
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statutFilter, setStatutFilter] = useState('all');
    const [activeCardFilter, setActiveCardFilter] = useState(null); // null | 'presta' | 'livraison' | 'acompte'
    const navigate = useNavigate();
    const { user } = useAuth();

    const fetchClients = (silent = false) => {
        axios.get(`${API}/api/clients`, { withCredentials: true })
            .then(res => setClients(prev => listsEqualById(prev, res.data) ? prev : res.data))
            .catch(() => { if (!silent) toast.error('Erreur lors du chargement'); })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchClients();
    }, []);

    useAutoRefresh(() => fetchClients(true));

    // Sync cross-onglets : si un autre onglet modifie un client, on re-fetch
    useEffect(() => {
        const unsub = subscribeDataChange(['client', 'facture'], () => fetchClients(true));
        return unsub;
    }, []);

    const clientLocks = useBulkLocks('client', (clients || []).map(c => c.id));

    const handleDelete = async (e, clientId) => {
        e.stopPropagation();
        if (!window.confirm('Supprimer ce client ?')) return;
        try {
            await axios.delete(`${API}/api/clients/${clientId}`, { withCredentials: true });
            toast.success('Client supprime');
            fetchClients();
        } catch { toast.error('Erreur'); }
    };

    const filtered = clients.filter(c => {
        if (activeCardFilter === 'presta' && !(c.statut_prestation === 'non_programme' && c.statut_client !== 'annule')) return false;
        if (activeCardFilter === 'livraison' && !(c.statut_livraison === 'a_programmer' && c.statut_client !== 'annule')) return false;
        if (activeCardFilter === 'acompte' && !(c.acompte === 'a_regle' && c.statut_client !== 'annule')) return false;
        if (statutFilter !== 'all' && (c.statut_client || 'a_verifier') !== statutFilter) return false;
        if (!search) return true;
        const s = search.toLowerCase();
        return (c.client_nom || '').toLowerCase().includes(s)
            || (c.client_telephone || '').includes(s)
            || (c.client_email || '').toLowerCase().includes(s)
            || (c.numero_devis || '').includes(s);
    });

    const sorted = sortClients(filtered);

    const prestaNonProg = clients.filter(c => c.statut_prestation === 'non_programme' && c.statut_client !== 'annule').length;
    const livraisonAProg = clients.filter(c => c.statut_livraison === 'a_programmer' && c.statut_client !== 'annule').length;
    const acompteARegler = clients.filter(c => c.acompte === 'a_regle' && c.statut_client !== 'annule').length;

    return (
        <div className="p-4 md:p-6 lg:p-8" data-testid="clients-page">
            <ReadOnlyGuard module="clients">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                <div className="flex items-center gap-3">
                    {activeCardFilter && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setActiveCardFilter(null)}
                            data-testid="clients-back-btn"
                        >
                            <ArrowLeft size={16} className="mr-1.5" />
                            Retour
                        </Button>
                    )}
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="clients-title">
                            Clients
                        </h1>
                        <p className="mt-1 text-slate-500">Demenagements confirmes</p>
                    </div>
                </div>
            </div>

            {/* Stats cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6" data-testid="clients-stats">
                <Card
                    className={`bg-white rounded-xl border shadow-sm cursor-pointer transition-all hover:shadow-md ${activeCardFilter === 'presta' ? 'border-amber-500 ring-2 ring-amber-200' : 'border-amber-200'}`}
                    onClick={() => setActiveCardFilter(prev => prev === 'presta' ? null : 'presta')}
                    data-testid="clients-stat-card-presta"
                >
                    <CardContent className="p-4 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                            <ClipboardList size={20} className="text-amber-600" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-amber-700" data-testid="clients-stat-presta">{prestaNonProg}</p>
                            <p className="text-xs text-slate-500">Prestation non programmée</p>
                        </div>
                    </CardContent>
                </Card>
                <Card
                    className={`bg-white rounded-xl border shadow-sm cursor-pointer transition-all hover:shadow-md ${activeCardFilter === 'livraison' ? 'border-blue-500 ring-2 ring-blue-200' : 'border-blue-200'}`}
                    onClick={() => setActiveCardFilter(prev => prev === 'livraison' ? null : 'livraison')}
                    data-testid="clients-stat-card-livraison"
                >
                    <CardContent className="p-4 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                            <Package size={20} className="text-blue-600" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-blue-700" data-testid="clients-stat-livraison">{livraisonAProg}</p>
                            <p className="text-xs text-slate-500">Livraison à programmer</p>
                        </div>
                    </CardContent>
                </Card>
                <Card
                    className={`bg-white rounded-xl border shadow-sm cursor-pointer transition-all hover:shadow-md ${activeCardFilter === 'acompte' ? 'border-rose-500 ring-2 ring-rose-200' : 'border-rose-200'}`}
                    onClick={() => setActiveCardFilter(prev => prev === 'acompte' ? null : 'acompte')}
                    data-testid="clients-stat-card-acompte"
                >
                    <CardContent className="p-4 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-rose-50 flex items-center justify-center">
                            <CreditCard size={20} className="text-rose-600" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-rose-700" data-testid="clients-stat-acompte">{acompteARegler}</p>
                            <p className="text-xs text-slate-500">Acompte à régler</p>
                        </div>
                    </CardContent>
                </Card>
            </div>

            <Card className="bg-white rounded-xl border border-slate-200 shadow-sm mb-6">
                <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="relative flex-1">
                            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <Input placeholder="Rechercher (nom, telephone, email, numero)..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10" data-testid="clients-search" />
                        </div>
                        <Select value={statutFilter} onValueChange={setStatutFilter}>
                            <SelectTrigger className="w-full sm:w-[200px]" data-testid="clients-statut-filter">
                                <SelectValue placeholder="Filtrer par statut" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tous les statuts</SelectItem>
                                {Object.entries(CLIENT_STATUTS).map(([val, info]) => (
                                    <SelectItem key={val} value={val}>{info.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>

            <Card className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg font-semibold text-slate-800">
                        <UserCheck size={20} className="text-[#2e7cd6]" />
                        Liste des clients ({filtered.length})
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-0 sm:p-6">
                    {loading ? (
                        <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-[#2e7cd6]" /></div>
                    ) : sorted.length === 0 ? (
                        <div className="text-center py-12 text-slate-500">Aucun client</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <Table className="min-w-[800px]" data-testid="clients-table">
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Client</TableHead>
                                        <TableHead>Date déménagement</TableHead>
                                        <TableHead>Livraison</TableHead>
                                        <TableHead>Acompte</TableHead>
                                        <TableHead>Statut</TableHead>
                                        <TableHead className="text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {sorted.map(c => {
                                        const iconColor = getIconColor(c.date_demenagement, c.statut_client);
                                        const isPast = c.date_demenagement
                                            ? differenceInCalendarDays(parseISO(c.date_demenagement), new Date()) < 0
                                            : false;
                                        const livInfo = LIVRAISON_STATUTS[c.statut_livraison || 'sans_livraison'] || LIVRAISON_STATUTS.sans_livraison;
                                        const acoInfo = ACOMPTE_STATUTS[c.acompte || 'a_regle'] || ACOMPTE_STATUTS.a_regle;
                                        const cliInfo = CLIENT_STATUTS[c.statut_client || 'a_verifier'] || CLIENT_STATUTS.a_verifier;
                                        return (
                                            <TableRow key={c.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/clients/${c.id}`)} data-testid={`client-row-${c.id}`}>
                                                <TableCell>
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                                                            <Truck size={18} className={iconColor} />
                                                        </div>
                                                        <div>
                                                            <p className="font-medium text-slate-900 flex items-center gap-2">
                                                                {c.client_nom || '-'}
                                                                <LockIndicator lock={clientLocks[c.id]} />
                                                            </p>
                                                            <p className="text-xs text-slate-400">{c.commercial_name}</p>
                                                        </div>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    {c.statut_prestation === 'programme' ? (
                                                        <div className="flex items-center gap-2">
                                                            <Calendar size={14} className={isPast ? 'text-red-400' : iconColor} />
                                                            <p className={`text-sm ${isPast ? 'text-red-400' : 'text-slate-700'}`}>
                                                                {formatDate(c.date_demenagement)}
                                                            </p>
                                                        </div>
                                                    ) : (
                                                        <Badge variant="outline" className="text-xs bg-red-100 text-red-700 border-red-200">
                                                            A programmer
                                                        </Badge>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <Badge variant="outline" className={`text-xs ${livInfo.color}`}>{livInfo.label}</Badge>
                                                </TableCell>
                                                <TableCell>
                                                    <Badge variant="outline" className={`text-xs ${acoInfo.color}`}>{acoInfo.label}</Badge>
                                                </TableCell>
                                                <TableCell>
                                                    <Badge variant="outline" className={`text-xs ${cliInfo.color}`}>{cliInfo.label}</Badge>
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <div className="flex items-center justify-end gap-1">
                                                        <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); navigate(`/clients/${c.id}`); }} data-testid={`client-view-${c.id}`}>
                                                            <Eye size={16} className="text-slate-600" />
                                                        </Button>
                                                        {user?.role === 'admin' && (
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                onClick={async (e) => {
                                                                    e.stopPropagation();
                                                                    if (!window.confirm(`Supprimer définitivement le client ${c.client_nom} ? Action irréversible.`)) return;
                                                                    try {
                                                                        await axios.delete(`${API}/api/clients/${c.id}`, { withCredentials: true });
                                                                        toast.success('Client supprimé');
                                                                        fetchClients();
                                                                    } catch (err) {
                                                                        toast.error('Erreur: ' + (err?.response?.data?.detail || err?.message));
                                                                    }
                                                                }}
                                                                data-testid={`client-delete-${c.id}`}
                                                                title="Supprimer (admin)"
                                                            >
                                                                <Trash2 size={16} className="text-red-500" />
                                                            </Button>
                                                        )}
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </CardContent>
            </Card>
            </ReadOnlyGuard>
        </div>
    );
}
